import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    TextField,
    Autocomplete,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Route as RouteIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ArrowUpward as MoveUpIcon,
    ArrowDownward as MoveDownIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import routeService from '../../services/routeService';
import cityService from '../../services/cityService';
import DataTable from '../../components/common/DataTable';

const DAYS_OF_WEEK = [
    { value: 'monday', label: 'Lunes' },
    { value: 'tuesday', label: 'Martes' },
    { value: 'wednesday', label: 'Miércoles' },
    { value: 'thursday', label: 'Jueves' },
    { value: 'friday', label: 'Viernes' },
    { value: 'saturday', label: 'Sábado' },
    { value: 'sunday', label: 'Domingo' }
];

const getDayLabel = (dayValue) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayValue);
    return day ? day.label : 'Sin asignar';
};

const RoutesSection = () => {
    const [expanded, setExpanded] = useState(false);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    
    // Form States
    const [routeName, setRouteName] = useState('');
    const [dayOfWeek, setDayOfWeek] = useState('');
    const [selectedStops, setSelectedStops] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [selectedCityOption, setSelectedCityOption] = useState(null);
    
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchRoutes = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await routeService.getRoutes();
            setRoutes(data);
        } catch (err) {
            setError('Error al obtener la lista de rutas.');
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveCities = async () => {
        try {
            const data = await cityService.getCities({
                paginate: 'false',
                is_available_for_trips: 'true',
                active_only: 'true'
            });
            setCityOptions(data);
        } catch (err) {
            console.error('Error al cargar ciudades disponibles para rutas:', err);
        }
    };

    useEffect(() => {
        if (expanded) {
            fetchRoutes();
            fetchActiveCities();
        }
    }, [expanded]);

    const handleOpenCreate = () => {
        setEditingRoute(null);
        setRouteName('');
        setDayOfWeek('');
        setSelectedStops([]);
        setSelectedCityOption(null);
        setError('');
        setOpenDialog(true);
    };

    const handleOpenEdit = (route) => {
        setEditingRoute(route);
        setRouteName(route.name);
        setDayOfWeek(route.day_of_week || '');
        // Map stops
        const sortedStops = [...route.route_cities].sort((a, b) => a.order - b.order).map((rc) => ({
            id: rc.id,
            cityId: rc.city,
            name: rc.city_name,
            province: rc.province_name,
        }));
        setSelectedStops(sortedStops);
        setSelectedCityOption(null);
        setError('');
        setOpenDialog(true);
    };

    const handleAddStop = () => {
        if (!selectedCityOption) return;
        
        // Evitar duplicados
        if (selectedStops.some((stop) => stop.cityId === selectedCityOption.id)) {
            setError('Esta ciudad ya forma parte de la ruta.');
            return;
        }

        setSelectedStops((prev) => [
            ...prev,
            {
                cityId: selectedCityOption.id,
                name: selectedCityOption.name,
                province: selectedCityOption.province?.name || '',
            }
        ]);
        setSelectedCityOption(null);
        setError('');
    };

    const handleRemoveStop = (index) => {
        setSelectedStops((prev) => prev.filter((_, i) => i !== index));
    };

    const handleMoveStop = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === selectedStops.length - 1) return;

        const newStops = [...selectedStops];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap
        const temp = newStops[index];
        newStops[index] = newStops[targetIndex];
        newStops[targetIndex] = temp;

        setSelectedStops(newStops);
    };

    const handleSave = async () => {
        if (!routeName.trim()) {
            setError('Debe especificar un nombre para la ruta.');
            return;
        }
        if (selectedStops.length === 0) {
            setError('Debe seleccionar al menos una ciudad para la ruta.');
            return;
        }

        const routeData = {
            name: routeName,
            day_of_week: dayOfWeek || null,
            route_cities: selectedStops.map((stop, index) => ({
                city: stop.cityId,
                order: index
            }))
        };

        try {
            setError('');
            if (editingRoute) {
                await routeService.updateRoute(editingRoute.id, routeData);
                setSuccess(`Ruta "${routeName}" actualizada con éxito.`);
            } else {
                await routeService.createRoute(routeData);
                setSuccess(`Ruta "${routeName}" creada con éxito.`);
            }
            setOpenDialog(false);
            fetchRoutes();
        } catch (err) {
            setError(err?.response?.data?.error || 'Error al guardar la ruta. Intente nuevamente.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de que desea eliminar esta ruta?')) {
            return;
        }
        try {
            setError('');
            await routeService.deleteRoute(id);
            setSuccess('Ruta eliminada exitosamente.');
            fetchRoutes();
        } catch (err) {
            setError('Error al eliminar la ruta.');
        }
    };

    const formatStopsPreview = (row) => {
        const sorted = [...row.route_cities].sort((a, b) => a.order - b.order);
        return sorted.map((rc) => rc.city_name).join(' → ');
    };

    const columns = [
        { id: 'name', label: 'Nombre de la Ruta', sx: { fontWeight: 600, width: '25%' } },
        {
            id: 'day_of_week',
            label: 'Día de Reparto',
            sx: { width: '20%' },
            render: (row) => (
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#0e141b' }}>
                    {getDayLabel(row.day_of_week)}
                </Typography>
            )
        },
        { 
            id: 'sequence', 
            label: 'Secuencia de Ciudades', 
            render: (row) => (
                <Typography sx={{ fontSize: '0.875rem', color: '#4e7397' }}>
                    {formatStopsPreview(row) || 'Sin ciudades configuradas'}
                </Typography>
            )
        },
    ];

    return (
        <Accordion
            expanded={expanded}
            onChange={(e, exp) => setExpanded(exp)}
            sx={{
                mb: 2,
                borderRadius: '0.75rem',
                border: '1px solid #d0dbe7',
                boxShadow: 'none',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#800020' }} />}
                sx={{
                    bgcolor: '#f6f7f8',
                    borderRadius: expanded ? '0.75rem 0.75rem 0 0' : '0.75rem',
                    '&:hover': { bgcolor: '#f0f0f0' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <RouteIcon sx={{ color: '#800020' }} />
                    <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>Rutas de Reparto</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' } }}
                    >
                        Nueva Ruta
                    </Button>
                </Box>

                <DataTable
                    columns={columns}
                    rows={routes}
                    loading={loading}
                    actions={(row) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" onClick={() => handleOpenEdit(row)} sx={{ color: '#4e7397' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                />

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        {editingRoute ? 'Editar Ruta de Reparto' : 'Nueva Ruta de Reparto'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2, pb: 4 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="Nombre de la Ruta"
                                variant="outlined"
                                value={routeName}
                                onChange={(e) => setRouteName(e.target.value)}
                                fullWidth
                                placeholder="Ej: Ruta Norte, Buenos Aires Express..."
                            />

                            <FormControl fullWidth>
                                <InputLabel id="day-of-week-label">Día de la Semana</InputLabel>
                                <Select
                                    labelId="day-of-week-label"
                                    id="day-of-week-select"
                                    value={dayOfWeek}
                                    label="Día de la Semana"
                                    onChange={(e) => setDayOfWeek(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Sin asignar</em>
                                    </MenuItem>
                                    {DAYS_OF_WEEK.map((day) => (
                                        <MenuItem key={day.value} value={day.value}>
                                            {day.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#0e141b' }}>
                                    Agregar Ciudad a la Secuencia:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Autocomplete
                                        id="route-city-select"
                                        options={cityOptions}
                                        getOptionLabel={(option) => `${option.name}, ${option.province?.name || ''}`}
                                        value={selectedCityOption}
                                        onChange={(event, newValue) => setSelectedCityOption(newValue)}
                                        sx={{ flex: 1 }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Seleccionar Ciudad" variant="outlined" size="small" />
                                        )}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={handleAddStop}
                                        startIcon={<AddIcon />}
                                        sx={{ color: '#800020', borderColor: '#800020', '&:hover': { bgcolor: 'rgba(128,0,32,0.04)', borderColor: '#800020' } }}
                                    >
                                        Agregar
                                    </Button>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#0e141b' }}>
                                    Secuencia / Orden de Recorrido:
                                </Typography>
                                {selectedStops.length === 0 ? (
                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', py: 2, textAlign: 'center', border: '1px dashed #d0dbe7', borderRadius: '0.5rem' }}>
                                        No has agregado ciudades a la ruta todavía.
                                    </Typography>
                                ) : (
                                    <List sx={{ border: '1px solid #d0dbe7', borderRadius: '0.5rem', bgcolor: 'white', p: 0 }}>
                                        {selectedStops.map((stop, index) => (
                                            <ListItem 
                                                key={stop.cityId} 
                                                sx={{ 
                                                    borderBottom: index === selectedStops.length - 1 ? 'none' : '1px solid #f0f0f0',
                                                    '&:hover': { bgcolor: '#f6f7f8' }
                                                }}
                                            >
                                                <ListItemText 
                                                    primary={`${index + 1}. ${stop.name}`} 
                                                    secondary={stop.province}
                                                    primaryTypographyProps={{ sx: { fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' } }}
                                                />
                                                <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton 
                                                        size="small" 
                                                        disabled={index === 0} 
                                                        onClick={() => handleMoveStop(index, 'up')}
                                                        sx={{ color: '#4e7397' }}
                                                    >
                                                        <MoveUpIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton 
                                                        size="small" 
                                                        disabled={index === selectedStops.length - 1} 
                                                        onClick={() => handleMoveStop(index, 'down')}
                                                        sx={{ color: '#4e7397' }}
                                                    >
                                                        <MoveDownIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleRemoveStop(index)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button onClick={() => setOpenDialog(false)} sx={{ color: '#4e7397', textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!routeName.trim() || selectedStops.length === 0}
                            sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' }, textTransform: 'none', borderRadius: '0.5rem' }}
                        >
                            Guardar Ruta
                        </Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
};

export default RoutesSection;
